import React, { useEffect, useState } from 'react';
import { FiChevronDown } from 'react-icons/fi';
import { api } from '../../services/apiClient';
import styles from './RankingHome.module.scss';

const MEDALHAS = { 1: '🥇', 2: '🥈', 3: '🥉' };

const formatarReal = (valor) =>
  (Number(valor) || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

/**
 * Ranking de quem mais comprou o bolão/loteria da campanha ativa.
 * A campanha, os prêmios em PIX e o recorte (bolão ou loteria) são definidos
 * no painel administrativo. O nome do cliente chega da API já abreviado
 * (só as 3 primeiras letras) — o site nunca recebe o nome completo.
 *
 * Na home mostramos só o pódio; do 4º lugar em diante fica atrás do botão
 * "ver ranking completo" para o bloco não empurrar os bolões pra baixo.
 */
export default function RankingHome() {
  const [dados, setDados] = useState(null);
  const [expandido, setExpandido] = useState(false);

  useEffect(() => {
    let ativo = true;

    const buscarRanking = async () => {
      try {
        const resposta = await api.get('/ranking_home');
        if (ativo) setDados(resposta.data);
      } catch (error) {
        console.log('Erro ao buscar ranking', error.message);
      }
    };

    buscarRanking();
    return () => {
      ativo = false;
    };
  }, []);

  const campanha = dados?.campanha;
  const ranking = dados?.ranking || [];
  const premios = dados?.premios || [];

  // Sem campanha ativa (ou sem ninguém no ranking ainda) o bloco não aparece.
  if (!campanha || ranking.length === 0) return null;

  const porValor = campanha.criterio === 'valor';
  const premioTotal = premios.reduce((soma, p) => soma + (Number(p.valor_pix) || 0), 0);
  const podio = ranking.slice(0, 3);
  const demais = ranking.slice(3);

  const metrica = (linha) => (porValor ? formatarReal(linha.total_valor) : `${linha.total_cotas} cotas`);

  return (
    <section className={styles.secao}>
      <div className={styles.cabecalho}>
        <div className={styles.tituloBloco}>
          <span className={styles.chamada}>RANKING</span>
          <h2 className={styles.titulo}>{campanha.nome}</h2>
          {campanha.alvo_nome && <span className={styles.alvo}>{campanha.alvo_nome}</span>}
        </div>
        {premioTotal > 0 && (
          <span className={styles.premioTotal}>{formatarReal(premioTotal)} em PIX</span>
        )}
      </div>

      {campanha.descricao && <p className={styles.descricao}>{campanha.descricao}</p>}

      <div className={styles.podio}>
        {podio.map((linha) => (
          <div key={linha.posicao} className={`${styles.card} ${styles[`lugar${linha.posicao}`]}`}>
            <span className={styles.medalha}>{MEDALHAS[linha.posicao]}</span>
            <div className={styles.cardInfo}>
              <span className={styles.nome}>{linha.nome}</span>
              <span className={styles.metrica}>{metrica(linha)}</span>
            </div>
            {linha.premio_pix > 0 && (
              <span className={styles.premio}>{formatarReal(linha.premio_pix)}</span>
            )}
          </div>
        ))}
      </div>

      {demais.length > 0 && (
        <>
          <button
            type="button"
            className={styles.botaoVerMais}
            onClick={() => setExpandido((atual) => !atual)}
            aria-expanded={expandido}
          >
            {expandido ? 'Ver menos' : `Ver ranking completo (${demais.length} a mais)`}
            <FiChevronDown className={`${styles.seta} ${expandido ? styles.setaAberta : ''}`} />
          </button>

          {expandido && (
            <ul className={styles.lista}>
              {demais.map((linha) => (
                <li key={linha.posicao} className={styles.linha}>
                  <span className={styles.posicao}>{linha.posicao}º</span>
                  <span className={styles.nomeLista}>{linha.nome}</span>
                  <span className={styles.metricaLista}>{metrica(linha)}</span>
                  <span className={styles.premioLista}>
                    {linha.premio_pix > 0 ? formatarReal(linha.premio_pix) : '—'}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </>
      )}

      <p className={styles.rodape}>Mostramos apenas as 3 primeiras letras do nome de cada participante.</p>
    </section>
  );
}
